import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { formatStatus } from '../utils/formatStatus';
import '../styles/UserOrderDetails.css';
import Toast from './Toast';
import getImgSrc from '../utils/getImgSrc';
import ButtonRegular from './ui/ButtonRegular';

function UserOrderDetails() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [order, setOrder] = useState(null);
	const [loading, setLoading] = useState(false);
	const [toast, setToast] = useState('');
	const [regulationAccepted, setRegulationAccepted] = useState(false);

	const apiUrl = process.env.REACT_APP_API_URL || '';

	useEffect(() => {
		const fetchOrder = async () => {
			setLoading(true);
			try {
				const res = await axios.get(`${apiUrl}/api/orders/${id}`, {
					headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
				});
				setOrder(res.data);
			} catch (err) {
				console.error('Błąd pobierania zamówienia:', err);
				setToast('❌ Nie udało się pobrać zamówienia');
			} finally {
				setLoading(false);
			}
		};

		fetchOrder();
	}, [apiUrl, id]);

	if (loading) return <p>Ładowanie...</p>;
	if (!order) return <p>Brak danych zamówienia.</p>;

	// Fallback danych klienta
	const client = order.clientAddress || order.user || order.shippingAddress || {};
	const clientName = `${client.firstName || ''} ${client.lastName || ''}`.trim() || '—';
	const clientEmail =
		client.email || order.shippingAddress?.email || order.user?.email || '—';
	const clientPhone = client.phone || order.user?.phone || '—';

	const clientAddress = {
		street: client.street || order.shippingAddress?.street || '—',
		buildingNumber:
			client.building || client.buildingNumber || order.shippingAddress?.building || '',
		flatNumber:
			client.apartment || client.flatNumber || order.shippingAddress?.apartment || '',
		city: client.city || order.shippingAddress?.city || '—',
		postalCode: client.postalCode || order.shippingAddress?.postalCode || '—',
		country: client.country || order.shippingAddress?.country || '—',
	};

	const handlePayment = async () => {
	if (!regulationAccepted || loading) return;

	setLoading(true);
	try {
		const token = localStorage.getItem('authToken');
		const res = await axios.post(
			`${apiUrl}/api/payment/p24/create-transaction`,
			{
				orderNumber: order.orderNumber || order._id,
				regulationAccept: true,
			},
			{ headers: { Authorization: `Bearer ${token}` } }
		);

		if (!res.data?.success || !res.data.redirectUrl) {
			throw new Error(res.data?.message || 'Nie udało się utworzyć płatności');
		}

		// przekierowanie do Przelewy24
		window.location.href = res.data.redirectUrl;
	} catch (err) {
		console.error(err);
		setToast(err.response?.data?.message || err.message || 'Błąd inicjacji płatności');
		setLoading(false);
	}
};

	return (
		<div className="order-detail-page admin-card order-grid">
			<ButtonRegular className="small ghost" onClick={() => navigate(-1)}>‹ Powrót</ButtonRegular>
			<h2 className="order-title">
				Twoje zamówienie {order.orderNumber || order._id}
				<span className={`status-badge ${order.status || 'pending'}`}>
					{formatStatus(order.status || 'pending')}
				</span>
			</h2>

			<div className="order-grid-inner">
				<div className="order-main">
					{/* Data zamówienia */}
					<div className="section small">
						<strong>Data:</strong>{' '}
						{order.createdAt ? new Date(order.createdAt).toLocaleString() : ''}
					</div>

					{/* Dane klienta */}
					<div className="section card-block">
						<h3>Twoje dane</h3>
						<div className="info-grid">
							<div>
								<strong>Imię i nazwisko:</strong> {clientName}
							</div>
							<div>
								<strong>Email:</strong> {clientEmail}
							</div>
							<div>
								<strong>Telefon:</strong> {clientPhone}
							</div>
						</div>
					</div>

					{/* Adres dostawy */}
					<div className="section card-block">
						<h3>Adres dostawy</h3>
						<div className="info-grid">
							<div>
								<strong>Ulica:</strong> {clientAddress.street}
							</div>
							<div>
								<strong>Numer budynku:</strong> {clientAddress.buildingNumber}
							</div>
							<div>
								<strong>Mieszkanie:</strong> {clientAddress.flatNumber}
							</div>
							<div>
								<strong>Miasto:</strong> {clientAddress.city}
							</div>
							<div>
								<strong>Kod pocztowy:</strong> {clientAddress.postalCode}
							</div>
							<div>
								<strong>Kraj:</strong> {clientAddress.country}
							</div>
						</div>
					</div>

					{/* Pozycje */}
					<div className="section card-block">
						<h3>Produkty w zamówieniu</h3>
						<div className="order-items-list">
							<div className="order-items-header">
								<div className="col-image">Produkt</div>
								<div className="col-name">Nazwa</div>
								<div className="col-qty">Ilość</div>
								<div className="col-unit">Cena jedn.</div>
								<div className="col-sub">Razem</div>
							</div>
							{(order.items || []).map((item, idx) => {
								const unit = (item.price || 0).toFixed(2);
								const qty = item.quantity || 1;
								const subtotal = (parseFloat(item.price || 0) * qty).toFixed(2);
								const img = item.image || item.product?.image || item.product?.images?.[0];
								const sku = item.sku || item.product?.sku || '';
								const opts = item.variant || item.options || '';

								return (
									<div className="order-item" key={item._id || idx}>
										<div className="col-image">
											{img ? (
												<img src={getImgSrc(img)} alt={item.name} className="item-image" />
											) : (
												<div className="item-image placeholder" />
											)}
										</div>
										<div className="col-name">
											<div className="item-name">
												{item.name || item.product?.name || 'Produkt'}
											</div>
											<div className="item-meta">
												{sku} {opts ? `• ${opts}` : ''}
											</div>
										</div>
										<div className="col-qty">{qty}</div>
										<div className="col-unit">{unit} PLN</div>
										<div className="col-sub">{subtotal} PLN</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>

				{/* Panel boczny */}
				<aside className="order-side">
					{/* Płatność */}
<div className="section card-block sticky-side">
	<h3>Płatność</h3>
	<p><strong>Metoda:</strong> {order.payment.method || '—'}</p>
	<p>
		<strong>Status:</strong> {(() => {
			if (!order.payment?.status) return '—';
			switch(order.payment.status) {
				case 'pending':
					return 'Oczekuje na zapłatę';
				case 'paid':
					return 'Opłacone';
				default:
					return order.payment.status;
			}
		})()}
	</p>
	{order.payment?.status === 'paid' ? (
		<p><strong>Data płatności:</strong> {new Date(order.payment.paidAt).toLocaleString()}</p>
	) : null}
	{order.payment?.method === 'Przelewy24' && order.payment?.status === 'pending' && (
	<div className="summary-actions">
		<div className="summary-terms">
			<input
				type="checkbox"
				checked={regulationAccepted}
				onChange={e => setRegulationAccepted(e.target.checked)}
			/>
			<label>
				Oświadczam, że zapoznałem się z{' '}
				<a href="https://www.przelewy24.pl/regulamin" target="_blank" rel="noopener noreferrer">
					regulaminem
				</a>{' '}
				oraz{' '}
				<a href="https://www.przelewy24.pl/obowiazek-informacyjny-rodo-platnicy" target="_blank" rel="noopener noreferrer">
					obowiązkiem informacyjnym
				</a>{' '}
				serwisu Przelewy24.
			</label>
		</div>

						<ButtonRegular
							onClick={handlePayment}
							disabled={loading || !regulationAccepted}
							className={`p24-button ${
								loading || !regulationAccepted ? 'disabled' : ''
							}`}
						>
							{loading ? (
								'Przekierowanie do płatności…'
							) : (
								<>
									<img
										src="/p24/Przelewy24_logo.png"
										alt="Przelewy24"
										className="p24-logo-img"
									/>
									<span>Opłać zamówienie</span>
								</>
							)}
						</ButtonRegular>
	</div>
)}
</div>


					{/* Dostawa */}
					<div className="section card-block sticky-side">
						<h3>Dostawa</h3>
						<p><strong>Metoda:</strong> {order.delivery?.label || '—'}</p>
					</div>

					{order.invoice && (
						<div className="section card-block sticky-side">
							<h3>Dane do faktury</h3>
							<div className="info-grid" style={{ marginTop: 8 }}>
								<div><strong>Nazwa firmy:</strong> {order.invoice.companyName || '—'}</div>
								<div><strong>NIP:</strong> {order.invoice.taxId || '—'}</div>
								<div><strong>REGON:</strong> {order.invoice.regon || '—'}</div>
								<div><strong>Ulica:</strong> {order.invoice.street || '—'}</div>
								<div><strong>Numer budynku:</strong> {order.invoice.building || '—'}</div>
								<div><strong>Mieszkanie:</strong> {order.invoice.apartment || '—'}</div>
								<div><strong>Miasto:</strong> {order.invoice.city || '—'}</div>
								<div><strong>Kod pocztowy:</strong> {order.invoice.postalCode || '—'}</div>
								<div><strong>Kraj:</strong> {order.invoice.country || '—'}</div>
							</div>
						</div>
					)}

					{/* Podsumowanie kosztów */}
					<div className="section card-block order-summary">
						<h3>Podsumowanie</h3>
						<div className="summary-row">
							<span>Kwota produktów:</span>
							<strong>{(order.total - (order.delivery?.price || 0)).toFixed(2)} PLN</strong>
						</div>
						<div className="summary-row">
							<span>Koszt dostawy:</span>
							<strong>{(order.delivery?.price || 0).toFixed(2)} PLN</strong>
						</div>
						<div className="summary-row">
							<span>Kwota całkowita:</span>
							<strong>{(order.total || 0).toFixed(2)} PLN</strong>
						</div>
						<div className="summary-row">
							<span>Nr zamówienia:</span>
							<span>{order.orderNumber || order._id}</span>
						</div>
						<div className="summary-row">
							<span>Data:</span>
							<span>{order.createdAt ? new Date(order.createdAt).toLocaleString() : ''}</span>
						</div>
						<div className="summary-row">
							<span>Status:</span>
							<span>{formatStatus(order.status || 'pending')}</span>
						</div>
					</div>
				</aside>
			</div>

			{toast && <Toast message={toast} onClose={() => setToast('')} />}
		</div>
	);
}
export default UserOrderDetails;
